/** Font metrics from Canvas 2D `measureText`, cached by font signature. */

export interface FontMetrics {
  /** Distance from baseline to top of capital letters (e.g. top of "M"). */
  capHeight: number;
  /** Distance from baseline to bottom of descenders (e.g. bottom of "g"). */
  descent: number;
  /** Distance from baseline to top of the font's design space (max ascender). */
  fontAscent: number;
  /** Distance from baseline to bottom of the font's design space (max descender). */
  fontDescent: number;
}

const cache = new Map<string, FontMetrics>();

export function getFontMetrics(
  fontFamily: string,
  fontSize: number,
  fontStyle: string = 'normal',
  fontWeight: string | number = 'normal'
): FontMetrics {
  const key = `${fontStyle}|${fontWeight}|${fontSize}|${fontFamily}`;
  const cached = cache.get(key);
  if (cached) return cached;

  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) {
    // SSR or no canvas support — approximate.
    return {
      capHeight: fontSize * 0.7,
      descent: fontSize * 0.2,
      fontAscent: fontSize * 0.85,
      fontDescent: fontSize * 0.2
    };
  }

  ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`;
  const m = ctx.measureText('M');
  const g = ctx.measureText('Mg');

  const metrics: FontMetrics = {
    capHeight: m.actualBoundingBoxAscent,
    descent: g.actualBoundingBoxDescent,
    fontAscent: m.fontBoundingBoxAscent,
    fontDescent: m.fontBoundingBoxDescent
  };

  cache.set(key, metrics);
  return metrics;
}
